import React, { useEffect, useRef, useState } from "react"
import axios from "axios"
import "../../../shared/styles/system.css"
import PatientFormDrawer from "../../patients/components/PatientFormDrawer"
import { ui } from "../../../shared/styles/uiSystem"

export default function NewAppointmentDrawer({
  open,
  clinicId,
  loadingPatients,
  patientQuery,
  setPatientQuery,
  newAppt,
  setNewAppt,
  patients,
  quickPatient,
  setQuickPatient,
  creatingPatient,
  createQuickPatient,
  treatments,
  onTreatmentChange,
  doctors,
  chairs = [],
  specialtyLabel,
  suggestDoctor,
  suggestingDoctor,
  loadAlternatives,
  loadingAlternatives,
  holdAppointment,
  createAppointment,
  loadingSuggestion,
  saving,
  doctorSuggestion,
  alternatives,
  formatDateTimeLocal,
  isReadOnly,
  readOnlyReason,
  onClose,
}) {
  const [openQuickPatientDrawer, setOpenQuickPatientDrawer] = useState(false)
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440,
  )
  const [selectedDate, setSelectedDate] = useState("")
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [slotError, setSlotError] = useState("")
  const [slotsPayload, setSlotsPayload] = useState(null)
  const [showDurationOverride, setShowDurationOverride] = useState(false)
  const isPlanLinkedMode = Boolean(newAppt.treatmentPlanItemId || newAppt.planTitle)
  const [patientSearchDraft, setPatientSearchDraft] = useState(patientQuery || "")

  const drawerRef = useRef(null)
  const patientQuerySyncRef = useRef(patientQuery || "")

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    const nextValue = patientQuery || ""

    if (!open) {
      patientQuerySyncRef.current = nextValue
      setPatientSearchDraft(nextValue)
      return
    }

    if (nextValue !== patientQuerySyncRef.current) {
      patientQuerySyncRef.current = nextValue
      setPatientSearchDraft(nextValue)
    }
  }, [open, patientQuery])

  useEffect(() => {
    if (!open || isPlanLinkedMode) return

    const timer = setTimeout(() => {
      const nextValue = patientSearchDraft || ""

      if (nextValue !== patientQuery) {
        patientQuerySyncRef.current = nextValue
        setPatientQuery(nextValue)
      }
    }, 320)

    return () => clearTimeout(timer)
  }, [open, isPlanLinkedMode, patientSearchDraft, patientQuery, setPatientQuery])

  useEffect(() => {
    if (!open || clinicId === "ALL") {
      setOpenQuickPatientDrawer(false)
    }
  }, [open, clinicId])

  useEffect(() => {
    if (!open) return

    if (doctors.length === 1) {
      setNewAppt((prev) =>
        prev.doctorId === doctors[0].id
          ? prev
          : {
              ...prev,
              doctorId: doctors[0].id,
            },
      )
    }
  }, [open, doctors, setNewAppt])

  useEffect(() => {
    if (!open) return

    if (chairs.length === 1) {
      setNewAppt((prev) =>
        prev.chairId === chairs[0].id
          ? prev
          : {
              ...prev,
              chairId: chairs[0].id,
            },
      )
    }
  }, [open, chairs, setNewAppt])

  useEffect(() => {
    if (!open) return

    const derivedDate = extractDatePart(newAppt.start)
    if (derivedDate) {
      setSelectedDate(derivedDate)
      return
    }

    const today = new Date()
    setSelectedDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
        today.getDate(),
      ).padStart(2, "0")}`,
    )
  }, [open, newAppt.start])

  useEffect(() => {
    if (!open || !clinicId || clinicId === "ALL") return
    if (!selectedDate) return
    if (!newAppt.treatmentId) {
      setSlotsPayload(null)
      setSlotError("")
      return
    }

    let cancelled = false

    const loadSlots = async () => {
      try {
        setLoadingSlots(true)
        setSlotError("")

        const { data } = await axios.post("/api/appointments/available-slots", {
          clinicId,
          date: selectedDate,
          treatmentId: newAppt.treatmentId,
          durationMinutes: Number(newAppt.duration || 30),
          doctorId: newAppt.doctorId || undefined,
          chairId: newAppt.chairId || undefined,
        })

        if (cancelled) return

        setSlotsPayload(data || null)

        if (data?.durationMinutes && Number(data.durationMinutes) !== Number(newAppt.duration || 0)) {
          setNewAppt((prev) => ({
            ...prev,
            duration: Number(data.durationMinutes || prev.duration || 30),
          }))
        }

        if (!newAppt.doctorId && data?.selectedDoctor?.id) {
          setNewAppt((prev) =>
            prev.doctorId
              ? prev
              : {
                  ...prev,
                  doctorId: data.selectedDoctor.id,
                },
          )
        }
      } catch (err) {
        if (cancelled) return

        const message =
          err?.response?.data?.message || "No se pudieron cargar los horarios disponibles"
        setSlotError(Array.isArray(message) ? message[0] : message)
        setSlotsPayload(null)
      } finally {
        if (!cancelled) {
          setLoadingSlots(false)
        }
      }
    }

    loadSlots()

    return () => {
      cancelled = true
    }
  }, [
    open,
    clinicId,
    selectedDate,
    newAppt.treatmentId,
    newAppt.duration,
    newAppt.doctorId,
    newAppt.chairId,
    setNewAppt,
  ])

  if (!open || clinicId === "ALL") return null

  const isMobile = viewportWidth < 760
  const selectedTreatment = treatments.find((t) => t.id === newAppt.treatmentId)
  const showDoctorSelect = doctors.length > 1
  const showChairSelect = chairs.length > 1
  const canUseSmartSuggestion = doctors.length > 1 && chairs.length > 1
  const hasSuggestion = Boolean(doctorSuggestion?.found)
  const hasAlternatives = Array.isArray(alternatives) && alternatives.length > 0

  const autoDoctorName = doctors.length === 1 ? doctors[0]?.name || "Doctor único" : ""
  const autoChairName = chairs.length === 1 ? chairs[0]?.name || "Silla única" : ""
  const safePatients = Array.isArray(patients) ? patients : []
  const selectedPatient = safePatients.find((p) => p.id === newAppt.patientId)
  const patientSearchText = patientSearchDraft.trim()
  const shouldShowPatientResults = !isPlanLinkedMode && patientSearchText.length >= 2

  const suggestedSlots = Array.isArray(slotsPayload?.suggested) ? slotsPayload.suggested : []
  const allSlots = Array.isArray(slotsPayload?.slots) ? slotsPayload.slots : []
  const selectedSlotLabel = newAppt.start ? formatReadableDateTime(newAppt.start) : ""
  const canSubmit =
    Boolean(newAppt.patientId) &&
    Boolean(newAppt.treatmentId) &&
    Boolean(newAppt.start) &&
    !saving &&
    !isReadOnly &&
    treatments.length > 0

  const goToTreatmentCatalog = () => {
    const target =
      clinicId && clinicId !== "ALL"
        ? `/treatment-catalog?clinicId=${encodeURIComponent(clinicId)}`
        : "/treatment-catalog"

    window.location.href = target
  }

  const goToScheduleSettings = () => {
    const target =
      clinicId && clinicId !== "ALL"
        ? `/settings/schedule-availability?clinicId=${encodeURIComponent(clinicId)}`
        : "/settings/schedule-availability"

    window.location.href = target
  }

  const buildLocalIsoFromParts = (datePart, timePart) => {
    if (!datePart || !timePart) return ""

    const [year, month, day] = String(datePart).split("-").map(Number)
    const [hour, minute] = String(timePart).split(":").map(Number)

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(hour) ||
      Number.isNaN(minute)
    ) {
      return ""
    }

    const localDate = new Date(year, month - 1, day, hour, minute, 0, 0)

    if (Number.isNaN(localDate.getTime())) return ""

    return localDate.toISOString()
  }

  const extractWallClockParts = (value) => {
    if (!value) {
      return {
        datePart: "",
        timePart: "",
      }
    }

    const source = String(value)

    if (source.includes("T") && source.length >= 16) {
      return {
        datePart: source.slice(0, 10),
        timePart: source.slice(11, 16),
      }
    }

    return {
      datePart: extractDatePart(source),
      timePart: "",
    }
  }

  const toLocalValue = (value) => {
    if (!value) return ""

    const source = String(value)

    if (source.includes("T") && source.length >= 16) {
      const { datePart, timePart } = extractWallClockParts(source)
      const isoValue = buildLocalIsoFromParts(datePart, timePart)

      if (isoValue) return isoValue
    }

    try {
      const dateValue = value instanceof Date ? value : new Date(value)

      if (!Number.isNaN(dateValue.getTime())) {
        return dateValue.toISOString()
      }
    } catch {
      // fallback abajo
    }

    return ""
  }

  const toLocalValueFromSlot = (slot) => {
    if (!slot) return ""

    const rawSlot = String(slot.startTime || "")
    const labelTime = String(slot.label || "").match(/\d{1,2}:\d{2}/)?.[0] || ""

    const rawTime =
      rawSlot.includes("T") && rawSlot.length >= 16
        ? rawSlot.slice(11, 16)
        : ""

    const slotDate = selectedDate || rawSlot.slice(0, 10)
    const slotTime = labelTime || rawTime

    if (!slotDate || !slotTime) return ""

    const [hourRaw, minuteRaw] = slotTime.split(":")
    const hour = String(hourRaw || "").padStart(2, "0")
    const minute = String(minuteRaw || "").padStart(2, "0")

    return `${slotDate}T${hour}:${minute}`
  }

  const applySmartSuggestion = () => {
    setNewAppt((prev) => ({
      ...prev,
      doctorId: doctorSuggestion?.doctor?.id || prev.doctorId,
      start:
        toLocalValue(doctorSuggestion?.startTime || doctorSuggestion?.suggestedStart) || prev.start,
      chairId: prev.chairId || (chairs.length === 1 ? chairs[0].id : prev.chairId),
    }))
  }

  const getPatientDisplayName = (patient) =>
    String(
      patient?.fullName ||
        `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim() ||
        "Paciente sin nombre",
    ).trim()

  const getPatientDetails = (patient) =>
    [patient?.phone, patient?.email].filter(Boolean).join(" · ")

  const handlePatientSearchChange = (event) => {
    const value = event.target.value
    setPatientSearchDraft(value)

    if (newAppt.patientId) {
      setNewAppt((prev) =>
        prev.patientId
          ? {
              ...prev,
              patientId: "",
            }
          : prev,
      )
    }
  }

  const handlePatientSelect = (patient) => {
    if (!patient?.id) return

    const fullName = getPatientDisplayName(patient)

    setNewAppt((prev) => ({
      ...prev,
      patientId: patient.id,
    }))

    setPatientSearchDraft(fullName)
    patientQuerySyncRef.current = fullName
    setPatientQuery(fullName)
  }

  const clearSelectedPatient = () => {
    setNewAppt((prev) => ({
      ...prev,
      patientId: "",
    }))

    setPatientSearchDraft("")
    patientQuerySyncRef.current = ""
    setPatientQuery("")
  }

  const handleQuickPatientSubmit = async () => {
    const createdPatient = await createQuickPatient()

    if (createdPatient?.id) {
      setNewAppt((prev) => ({
        ...prev,
        patientId: createdPatient.id,
      }))

      const fullName = `${createdPatient.firstName || ""} ${createdPatient.lastName || ""}`.trim()
      setPatientSearchDraft(fullName)
      patientQuerySyncRef.current = fullName
      setPatientQuery(fullName)
    }

    setOpenQuickPatientDrawer(false)
    setQuickPatient({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      birthDate: "",
    })
  }

  const handleUseExistingQuickPatient = async (candidate) => {
    if (!candidate?.id) return

    const fullName =
      candidate.fullName ||
      `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim()

    setNewAppt((prev) => ({
      ...prev,
      patientId: candidate.id
    }))

    setPatientSearchDraft(fullName)
    patientQuerySyncRef.current = fullName
    setPatientQuery(fullName)

    setOpenQuickPatientDrawer(false)
    setQuickPatient({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      birthDate: ""
    })

    alert("Paciente existente seleccionado.")
  }
  const selectSlot = (slot) => {
    if (!slot || slot.status !== "available") return

    setNewAppt((prev) => ({
      ...prev,
      start: toLocalValueFromSlot(slot),
      duration: Number(slotsPayload?.durationMinutes || prev.duration || 30),
      doctorId: prev.doctorId || slot.doctorId || prev.doctorId,
      chairId: prev.chairId || (chairs.length === 1 ? chairs[0].id : prev.chairId),
    }))
  }

  const handleDateChange = (value) => {
    setSelectedDate(value)

    setNewAppt((prev) => {
      const sameDate = extractDatePart(prev.start) === value

      return {
        ...prev,
        start: sameDate ? prev.start : "",
      }
    })
  }

  return (
    <>
      <PatientFormDrawer
        open={openQuickPatientDrawer}
        mode="create"
        form={quickPatient}
        setForm={setQuickPatient}
        onClose={() => {
          setOpenQuickPatientDrawer(false)
          setQuickPatient({
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            birthDate: "",
          })
        }}
        onSubmit={handleQuickPatientSubmit}
        onUseDuplicatePatient={handleUseExistingQuickPatient}
        saving={creatingPatient}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
      />

      <div
        className="of-drawer of-drawer--right of-drawer--md"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999998,
          background: "rgba(15,23,42,0.18)",
          display: "flex",
          justifyContent: "flex-end",
          backdropFilter: "blur(2px)",
        }}
        onMouseDown={() => {
          if (openQuickPatientDrawer) return
          onClose?.()
        }}
      >
        <div
          ref={drawerRef}
          className="of-drawer__inner"
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            background: "#f8fafc",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            className="of-drawer__header"
            style={{
              borderBottom: "1px solid #e2e8f0",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    display: "inline-flex",
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    color: "#1d4ed8",
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    marginBottom: 10,
                  }}
                >
                  Agenda inteligente
                </div>

                <h3
                  className="of-drawer__title"
                  style={{
                    lineHeight: 1.02,
                    fontSize: 26,
                    letterSpacing: "-0.03em",
                    marginBottom: 4,
                  }}
                >
                  Nueva cita
                </h3>

                {isPlanLinkedMode ? (
                  <div className="of-drawer__subtitle">
                    Ligada a plan: {newAppt.planTitle || "Plan de tratamiento"}
                  </div>
                ) : (
                  <div className="of-drawer__subtitle">
                    Flujo simple para agendar sin meter ruido innecesario
                  </div>
                )}
              </div>

              <button onClick={onClose} className="of-drawer__close">
                Cerrar
              </button>
            </div>
          </div>

          {isReadOnly ? (
            <div
              style={{
                ...ui.dangerCard,
                margin: "14px 16px 0",
                padding: 12,
                fontWeight: 700,
              }}
            >
              {readOnlyReason || "Modo solo lectura activo"}
            </div>
          ) : null}

          <div
            className="of-drawer__body"
            style={{
              paddingTop: 14,
              display: "grid",
              gap: 14,
            }}
          >
            <SectionCard
              title="Paciente"
              subtitle={
                isPlanLinkedMode
                  ? "El paciente ya viene definido por el plan."
                  : "Primero define a quién vas a atender"
              }
            >
              <div style={{ display: "grid", gap: 10 }}>
                <input
                  type="search"
                  inputMode="search"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="Nombre, apellido, teléfono o correo"
                  value={patientSearchDraft}
                  onChange={handlePatientSearchChange}
                  style={{
                    ...ui.input,
                    WebkitTapHighlightColor: "transparent",
                  }}
                  disabled={isPlanLinkedMode}
                />

                {!isPlanLinkedMode ? (
                  <div style={patientSearchBoxStyle}>
                    {selectedPatient ? (
                      <div style={selectedPatientCardStyle}>
                        <div style={{ minWidth: 0 }}>
                          <div style={selectedPatientTitleStyle}>
                            {getPatientDisplayName(selectedPatient)}
                          </div>
                          {getPatientDetails(selectedPatient) ? (
                            <div style={selectedPatientMetaStyle}>
                              {getPatientDetails(selectedPatient)}
                            </div>
                          ) : null}
                        </div>

                        <button
                          type="button"
                          onClick={clearSelectedPatient}
                          style={patientMiniButtonStyle}
                        >
                          Cambiar
                        </button>
                      </div>
                    ) : null}

                    {loadingPatients && safePatients.length === 0 ? (
                      <div style={patientSearchStatusStyle}>Buscando pacientes...</div>
                    ) : null}

                    {loadingPatients && safePatients.length > 0 ? (
                      <div style={patientSearchSoftStatusStyle}>Actualizando resultados...</div>
                    ) : null}

                    {!loadingPatients && patientSearchText.length === 1 ? (
                      <div style={patientSearchStatusStyle}>
                        Escribe al menos 2 caracteres para buscar.
                      </div>
                    ) : null}

                    {!loadingPatients && shouldShowPatientResults ? (
                      safePatients.length > 0 ? (
                        <div style={patientResultsListStyle}>
                          {safePatients.map((patient) => {
                            const isSelected = newAppt.patientId === patient.id
                            const details = getPatientDetails(patient)

                            return (
                              <button
                                key={patient.id}
                                type="button"
                                onClick={() => handlePatientSelect(patient)}
                                style={{
                                  ...patientResultButtonStyle,
                                  ...(isSelected ? patientResultButtonSelectedStyle : {}),
                                }}
                              >
                                <span style={{ minWidth: 0 }}>
                                  <span style={patientResultNameStyle}>
                                    {getPatientDisplayName(patient)}
                                  </span>
                                  {details ? (
                                    <span style={patientResultDetailsStyle}>{details}</span>
                                  ) : null}
                                </span>

                                <span style={patientResultActionStyle}>
                                  {isSelected ? "Seleccionado" : "Elegir"}
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      ) : (
                        <div style={patientEmptyStateStyle}>
                          No encontramos pacientes con esa búsqueda.
                        </div>
                      )
                    ) : null}
                  </div>
                ) : null}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ ...ui.metaText, fontSize: 12 }}>
                    {isPlanLinkedMode
                      ? "Paciente bloqueado por contexto del plan."
                      : "Se muestran hasta 20 resultados para mantenerlo rápido."}
                  </div>

                  {!isPlanLinkedMode ? (
                    <button
                      onClick={() => setOpenQuickPatientDrawer(true)}
                      disabled={isReadOnly}
                      style={ui.secondaryButton}
                    >
                      Alta rápida
                    </button>
                  ) : null}
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title={isPlanLinkedMode ? "Tratamiento del plan" : "Tratamiento"}
              subtitle={
                isPlanLinkedMode
                  ? "El tratamiento viene definido. Aquí solo ajustas la ejecución."
                  : "Luego define qué procedimiento se va a realizar"
              }
            >
              <div style={{ display: "grid", gap: 10 }}>
                <select
                  value={newAppt.treatmentId}
                  onChange={(e) => onTreatmentChange(e.target.value)}
                  style={ui.select}
                  disabled={isPlanLinkedMode}
                >
                  {treatments.length === 0 ? (
                    <option value="">No hay tratamientos registrados</option>
                  ) : (
                    <>
                      <option value="">Selecciona tratamiento</option>
                      {treatments.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>

                {isPlanLinkedMode ? (
                  <div style={{ ...ui.metaText, fontSize: 12 }}>
                    Tratamiento tomado del plan. Aquí solo defines ejecución y recursos.
                  </div>
                ) : null}

                {treatments.length === 0 ? (
                  <div
                    style={{
                      ...ui.infoCard,
                      padding: 12,
                      background: "#fff7ed",
                      border: "1px solid #fed7aa",
                    }}
                  >
                    <div style={{ fontSize: 13, color: "#9a3412", fontWeight: 700 }}>
                      Esta clínica aún no tiene tratamientos configurados.
                    </div>
                    <div style={{ fontSize: 12, color: "#7c2d12", marginTop: 4 }}>
                      Puedes crear o editar el catálogo y volver aquí de inmediato.
                    </div>

                    <button
                      onClick={goToTreatmentCatalog}
                      style={{ ...ui.secondaryButton, marginTop: 10 }}
                    >
                      Administrar tratamientos
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {!isPlanLinkedMode ? (
                      <button
                        onClick={goToTreatmentCatalog}
                        style={{
                          fontSize: 12,
                          color: "#2563eb",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          fontWeight: 700,
                        }}
                      >
                        + Administrar tratamientos
                      </button>
                    ) : null}

                    {selectedTreatment ? (
                      <div style={{ ...ui.metaText, fontSize: 12 }}>
                        Duración sugerida: {selectedTreatment.durationMinutes} min
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              title={isPlanLinkedMode ? "Ejecutar tratamiento" : "Horario y recursos"}
              subtitle={
                isPlanLinkedMode
                  ? "Solo define cuándo, con quién y en qué recurso se va a ejecutar."
                  : "Aquí se acomoda la parte operativa con horarios realmente agendables."
              }
            >
              {isPlanLinkedMode ? (
                <div
                  style={{
                    ...ui.infoCard,
                    padding: 12,
                    border: "1px solid #bfdbfe",
                    background: "#eff6ff",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#1d4ed8" }}>
                    Ejecución ligada a plan
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#1e3a8a",
                      marginTop: 4,
                      lineHeight: 1.4,
                    }}
                  >
                    El paciente y el tratamiento vienen definidos por el plan. Aquí solo ajustas
                    fecha, doctor, silla y mejor horario disponible.
                  </div>
                </div>
              ) : null}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: 10,
                }}
              >
                <div>
                  <label style={ui.label}>Fecha</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    style={ui.input}
                  />
                </div>

                <div>
                  <label style={ui.label}>Duración</label>
                  <div
                    style={{
                      ...ui.infoCard,
                      minHeight: 42,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "10px 12px",
                    }}
                  >
                    <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 800 }}>
                      {Number(newAppt.duration || selectedTreatment?.durationMinutes || 30)} min
                    </span>

                    <button
                      type="button"
                      onClick={() => setShowDurationOverride((prev) => !prev)}
                      style={{
                        fontSize: 12,
                        color: "#2563eb",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        fontWeight: 700,
                      }}
                    >
                      {showDurationOverride ? "Ocultar ajuste" : "Ajustar duración"}
                    </button>
                  </div>
                </div>
              </div>

              {showDurationOverride ? (
                <div style={{ marginTop: 10 }}>
                  <label style={ui.label}>Ajuste manual de duración (min)</label>
                  <input
                    type="number"
                    value={newAppt.duration}
                    onChange={(e) =>
                      setNewAppt((prev) => ({
                        ...prev,
                        duration: Number(e.target.value || 30),
                        start: "",
                      }))
                    }
                    style={ui.input}
                  />
                </div>
              ) : null}

              {showDoctorSelect ? (
                <div style={{ marginTop: 10 }}>
                  <label style={ui.label}>Doctor</label>

                  <select
                    value={newAppt.doctorId}
                    onChange={(e) =>
                      setNewAppt((prev) => ({
                        ...prev,
                        doctorId: e.target.value,
                        start: "",
                      }))
                    }
                    style={ui.select}
                  >
                    <option value="">Asignación automática</option>
                    {doctors.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name} · {specialtyLabel(d.specialty)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {showChairSelect ? (
                <div style={{ marginTop: 10 }}>
                  <label style={ui.label}>Silla</label>

                  <select
                    value={newAppt.chairId || ""}
                    onChange={(e) =>
                      setNewAppt((prev) => ({
                        ...prev,
                        chairId: e.target.value,
                        start: "",
                      }))
                    }
                    style={ui.select}
                  >
                    <option value="">Sin silla asignada</option>
                    {chairs.map((chair) => (
                      <option key={chair.id} value={chair.id}>
                        {chair.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {showDoctorSelect || showChairSelect ? (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginTop: 10,
                  }}
                >
                  {!showDoctorSelect && doctors.length === 1 ? (
                    <InlinePill tone="blue">Doctor automático: {autoDoctorName}</InlinePill>
                  ) : null}

                  {!showChairSelect && chairs.length === 1 ? (
                    <InlinePill tone="slate">Silla automática: {autoChairName}</InlinePill>
                  ) : null}
                </div>
              ) : null}

              {slotsPayload?.scheduleWarning ? (
                <div
                  style={{
                    ...ui.infoCard,
                    marginTop: 12,
                    padding: 12,
                    border: "1px solid #fde68a",
                    background: "#fffbeb",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#92400e" }}>
                    {slotsPayload.scheduleWarning}
                  </div>

                  <button
                    type="button"
                    onClick={goToScheduleSettings}
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      color: "#b45309",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      fontWeight: 800,
                    }}
                  >
                    Configurar horario
                  </button>
                </div>
              ) : null}

              {!newAppt.treatmentId ? (
                <div
                  style={{
                    ...ui.infoCard,
                    marginTop: 12,
                    padding: 12,
                  }}
                >
                  Primero selecciona un tratamiento para calcular horarios compatibles.
                </div>
              ) : loadingSlots ? (
                <div
                  style={{
                    ...ui.infoCard,
                    marginTop: 12,
                    padding: 12,
                    background: "#ecfeff",
                    border: "1px solid #a5f3fc",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#0f766e" }}>
                    Calculando horarios disponibles...
                  </div>
                </div>
              ) : slotError ? (
                <div
                  style={{
                    ...ui.dangerCard,
                    marginTop: 12,
                    padding: 12,
                  }}
                >
                  {slotError}
                </div>
              ) : (
                <>
                  <div style={{ marginTop: 12 }}>
                    <div style={ui.label}>Horarios sugeridos</div>

                    {suggestedSlots.length === 0 ? (
                      <div style={{ ...ui.metaText, fontSize: 12 }}>
                        No encontramos sugerencias rápidas para ese día.
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {suggestedSlots.map((slot) => (
                          <button
                            key={slot.startTime}
                            type="button"
                            onClick={() => selectSlot(slot)}
                            disabled={slot.status !== "available"}
                            style={{
                              ...slotChipStyle,
                              ...(isSlotSelected(newAppt.start, slot.startTime)
                                ? selectedSlotChipStyle
                                : slot.status === "available"
                                  ? availableSlotChipStyle
                                  : disabledSlotChipStyle),
                            }}
                          >
                            <span className="of-slot-chip-label">
                              {formatSlotDisplayLabel(slot)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={ui.label}>Todos los horarios del día</div>
                      <div style={{ ...ui.metaText, fontSize: 12 }}>
                        Solo se habilitan horarios compatibles con doctor, silla y duración.
                      </div>
                    </div>

                    <div style={slotsListStyle}>
                      {allSlots.map((slot) => (
                        <button
                          key={slot.startTime}
                          type="button"
                          onClick={() => selectSlot(slot)}
                          disabled={slot.status !== "available"}
                          style={{
                            ...slotRowStyle,
                            ...(isSlotSelected(newAppt.start, slot.startTime)
                              ? selectedSlotRowStyle
                              : slot.status === "available"
                                ? availableSlotRowStyle
                                : slot.status === "occupied"
                                  ? occupiedSlotRowStyle
                                  : slot.status === "duration_blocked"
                                    ? blockedSlotRowStyle
                                    : outsideSlotRowStyle),
                          }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontSize: 14, fontWeight: 900, whiteSpace: "nowrap" }}>
                              {formatSlotDisplayLabel(slot)}
                            </span>
                            <span style={{ fontSize: 11.5, opacity: 0.84 }}>
                              {slot.reason || "Disponible"}
                            </span>
                          </div>

                          <div style={{ textAlign: "right", minWidth: 110 }}>
                            <div style={{ fontSize: 11.5, fontWeight: 800 }}>
                              {slot.doctorName || "Sin doctor fijo"}
                            </div>
                            <div style={{ fontSize: 11, opacity: 0.78 }}>
                              {slot.status === "available"
                                ? "Disponible"
                                : slot.status === "occupied"
                                  ? "Ocupado"
                                  : slot.status === "duration_blocked"
                                    ? "No cabe duración"
                                    : "Fuera de horario"}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {selectedSlotLabel ? (
                    <div
                      style={{
                        ...ui.infoCard,
                        marginTop: 12,
                        padding: 12,
                        border: "1px solid #bfdbfe",
                        background: "#eff6ff",
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#1d4ed8" }}>
                        Horario seleccionado
                      </div>
                      <div style={{ marginTop: 4, color: "#1e3a8a", fontSize: 13 }}>
                        {selectedSlotLabel}
                      </div>
                    </div>
                  ) : null}
                </>
              )}

              {canUseSmartSuggestion ? (
                <div
                  style={{
                    marginTop: 12,
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={suggestDoctor}
                    disabled={suggestingDoctor || isReadOnly || !newAppt.treatmentId}
                    style={ui.secondaryButton}
                  >
                    {suggestingDoctor ? "Analizando..." : "Sugerencia inteligente"}
                  </button>

                  {(hasSuggestion || hasAlternatives) ? (
                    <button
                      onClick={loadAlternatives}
                      disabled={loadingAlternatives || isReadOnly || !newAppt.treatmentId}
                      style={{
                        ...ui.secondaryButton,
                        background: "#EFF6FF",
                        border: "1px solid #BFDBFE",
                        color: "#1D4ED8",
                      }}
                    >
                      {loadingAlternatives ? "Buscando..." : "Ver más opciones"}
                    </button>
                  ) : null}

                  <div style={{ ...ui.metaText, fontSize: 12 }}>
                    Úsalo como capa extra para optimizar doctor y ejecución.
                  </div>
                </div>
              ) : null}

              {loadingSuggestion ? (
                <div
                  style={{
                    ...ui.infoCard,
                    marginTop: 12,
                    padding: 12,
                    background: "#ecfeff",
                    border: "1px solid #a5f3fc",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#0f766e" }}>
                    Analizando disponibilidad clínica...
                  </div>
                </div>
              ) : null}

              {hasSuggestion ? (
                <div
                  style={{
                    ...ui.card,
                    marginTop: 12,
                    padding: 12,
                    border: "1px solid #bfdbfe",
                    background: "linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 900,
                        color: "#0f172a",
                      }}
                    >
                      Sugerencia inteligente
                    </div>

                    <InlinePill tone="blue">
                      Score: {Number(doctorSuggestion?.score || 0).toFixed(2)}
                    </InlinePill>
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    <SuggestionRow label="Doctor" value={doctorSuggestion?.doctor?.name} />
                    <SuggestionRow
                      label="Especialidad"
                      value={specialtyLabel(doctorSuggestion?.doctor?.specialty)}
                    />
                    <SuggestionRow label="Motivo" value={doctorSuggestion?.reason} />
                    <SuggestionRow
                      label="Horario"
                      value={
                        doctorSuggestion?.startTime
                          ? new Date(doctorSuggestion.startTime).toLocaleString("es-MX")
                          : doctorSuggestion?.suggestedStart
                            ? new Date(doctorSuggestion.suggestedStart).toLocaleString("es-MX")
                            : newAppt.start || "Sin horario sugerido"
                      }
                    />
                    {chairs.length === 1 ? (
                      <SuggestionRow label="Silla" value={autoChairName} />
                    ) : null}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 12,
                    }}
                  >
                    <button
                      style={{
                        ...ui.secondaryButton,
                        background: "#dbeafe",
                        border: "1px solid #bfdbfe",
                        color: "#1d4ed8",
                      }}
                      onClick={applySmartSuggestion}
                      disabled={isReadOnly}
                    >
                      Aplicar sugerencia
                    </button>
                  </div>
                </div>
              ) : null}

              {hasAlternatives ? (
                <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                  {alternatives.slice(0, 3).map((alt, idx) => (
                    <div
                      key={idx}
                      style={{
                        ...ui.card,
                        padding: 12,
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {alt.doctorName} · {specialtyLabel(alt.specialty)}
                      </div>

                      <div style={{ ...ui.metaText, marginTop: 4 }}>
                        {new Date(alt.startTime).toLocaleString("es-MX")}
                      </div>

                      <div style={{ ...ui.metaText, marginTop: 4 }}>
                        {alt.compatibilityReason}
                      </div>

                      <button
                        onClick={() => {
                          setNewAppt((prev) => ({
                            ...prev,
                            doctorId: alt.doctorId,
                            start: toLocalValue(alt.startTime) || prev.start,
                          }))
                        }}
                        disabled={isReadOnly}
                        style={{
                          ...ui.secondaryButton,
                          marginTop: 8,
                          background: "#dbeafe",
                          border: "1px solid #bfdbfe",
                          color: "#1d4ed8",
                        }}
                      >
                        Usar esta opción
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </SectionCard>
          </div>

          <div
            style={{
              borderTop: "1px solid #E2E8F0",
              padding: "14px 16px",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button onClick={onClose} style={ui.secondaryButton}>
              Cancelar
            </button>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={holdAppointment}
                disabled={!canSubmit}
                style={{
                  ...ui.secondaryButton,
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  color: "#c2410c",
                  opacity: canSubmit ? 1 : 0.7,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                }}
              >
                {saving ? "Guardando..." : "Reservar"}
              </button>

              <button
                onClick={createAppointment}
                disabled={!canSubmit}
                style={{
                  ...ui.primaryButton,
                  opacity: canSubmit ? 1 : 0.7,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                }}
              >
                {saving ? "Creando..." : "Crear cita"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function extractDatePart(value) {
  if (!value) return ""
  const source = String(value)
  if (source.includes("T")) return source.slice(0, 10)

  const date = new Date(source)
  if (Number.isNaN(date.getTime())) return ""

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`
}

function formatReadableDateTime(value) {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  return date.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  })
}

function formatSlotDisplayLabel(slot) {
  const label = String(slot?.label || "")
  const labelMatch = label.match(/\b(\d{1,2}):(\d{2})\b/)

  if (labelMatch?.[0]) {
    return formatHour12FromTime(labelMatch[0])
  }

  const startTime = String(slot?.startTime || "")

  if (startTime.includes("T") && startTime.length >= 16) {
    return formatHour12FromTime(startTime.slice(11, 16))
  }

  if (/^\d{1,2}:\d{2}$/.test(startTime)) {
    return formatHour12FromTime(startTime)
  }

  return label || startTime || ""
}

function formatHour12FromTime(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})$/)

  if (!match) return String(value || "")

  const hour24 = Number(match[1])
  const minute = match[2]

  if (Number.isNaN(hour24)) return String(value || "")

  const suffix = hour24 >= 12 ? "p.m." : "a.m."
  const hour12 = hour24 % 12 || 12

  return `${hour12}:${minute} ${suffix}`
}

function toLocalDateTimeInputValue(value) {
  if (!value) return ""

  const date = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(date.getTime())) {
    const source = String(value)
    return source.includes("T") && source.length >= 16 ? source.slice(0, 16) : ""
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}T${String(date.getHours()).padStart(
    2,
    "0",
  )}:${String(date.getMinutes()).padStart(2, "0")}`
}

function toWallClockDateTimeInputValue(value) {
  if (!value) return ""

  const source = String(value)

  if (source.includes("T") && source.length >= 16) {
    return source.slice(0, 16)
  }

  return toLocalDateTimeInputValue(value)
}

function isSlotSelected(currentStart, slotStart) {
  if (!currentStart || !slotStart) return false

  const current = toLocalDateTimeInputValue(currentStart)
  const slotLocal = toWallClockDateTimeInputValue(slotStart)

  return current === slotLocal
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div
      style={{
        ...ui.card,
        padding: 16,
        borderRadius: 18,
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </div>

        {subtitle ? (
          <div
            style={{
              fontSize: 12.5,
              color: "#64748b",
              marginTop: 4,
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  )
}

function SuggestionRow({ label, value }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "110px minmax(0, 1fr)",
        gap: 10,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          fontWeight: 800,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 14,
          color: "#0f172a",
          fontWeight: 800,
          wordBreak: "break-word",
          lineHeight: 1.35,
        }}
      >
        {value}
      </div>
    </div>
  )
}

function InlinePill({ children, tone = "slate" }) {
  const tones = {
    blue: {
      background: "#EFF6FF",
      border: "#BFDBFE",
      color: "#1D4ED8",
    },
    slate: {
      background: "#F8FAFC",
      border: "#CBD5E1",
      color: "#334155",
    },
  }

  const visual = tones[tone] || tones.slate

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        background: visual.background,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {children}
    </span>
  )
}

const patientSearchBoxStyle = {
  display: "grid",
  gap: 8,
}

const selectedPatientCardStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  borderRadius: 14,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
}

const selectedPatientTitleStyle = {
  fontSize: 13,
  fontWeight: 900,
  color: "#1d4ed8",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

const selectedPatientMetaStyle = {
  marginTop: 2,
  fontSize: 12,
  fontWeight: 700,
  color: "#1e3a8a",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

const patientMiniButtonStyle = {
  border: "1px solid #bfdbfe",
  background: "#ffffff",
  color: "#1d4ed8",
  borderRadius: 999,
  padding: "6px 10px",
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
  flex: "0 0 auto",
}

const patientSearchSoftStatusStyle = {
  padding: "6px 10px",
  borderRadius: 999,
  background: "#eef2ff",
  border: "1px solid #c7d2fe",
  color: "#4338ca",
  fontSize: 11,
  fontWeight: 900,
  width: "fit-content",
}

const patientSearchStatusStyle = {
  padding: "10px 12px",
  borderRadius: 14,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  fontSize: 12,
  fontWeight: 800,
}

const patientResultsListStyle = {
  display: "grid",
  gap: 8,
  maxHeight: 230,
  overflowY: "auto",
  paddingRight: 2,
  WebkitOverflowScrolling: "touch",
}

const patientResultButtonStyle = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: "11px 12px",
  borderRadius: 14,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  color: "#0f172a",
  textAlign: "left",
  cursor: "pointer",
}

const patientResultButtonSelectedStyle = {
  border: "1px solid #93c5fd",
  background: "#eff6ff",
}

const patientResultNameStyle = {
  display: "block",
  fontSize: 13.5,
  fontWeight: 900,
  color: "#0f172a",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

const patientResultDetailsStyle = {
  display: "block",
  marginTop: 2,
  fontSize: 12,
  fontWeight: 700,
  color: "#64748b",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

const patientResultActionStyle = {
  flex: "0 0 auto",
  fontSize: 11,
  fontWeight: 900,
  color: "#2563eb",
}

const patientEmptyStateStyle = {
  padding: "11px 12px",
  borderRadius: 14,
  border: "1px solid #fed7aa",
  background: "#fff7ed",
  color: "#9a3412",
  fontSize: 12,
  fontWeight: 800,
}

const slotsListStyle = {
  display: "grid",
  gap: 8,
  maxHeight: 280,
  overflowY: "auto",
  paddingRight: 4,
  marginTop: 8,
}

const slotRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  alignItems: "center",
  width: "100%",
  textAlign: "left",
  borderRadius: 14,
  padding: "11px 12px",
  border: "1px solid #e2e8f0",
  cursor: "pointer",
  transition: "all 0.15s ease",
}

const availableSlotRowStyle = {
  background: "#ffffff",
  color: "#0f172a",
}

const selectedSlotRowStyle = {
  background: "#eff6ff",
  border: "1px solid #93c5fd",
  color: "#1d4ed8",
}

const occupiedSlotRowStyle = {
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  cursor: "not-allowed",
}

const blockedSlotRowStyle = {
  background: "#f8fafc",
  border: "1px solid #cbd5e1",
  color: "#475569",
  cursor: "not-allowed",
}

const outsideSlotRowStyle = {
  background: "#f1f5f9",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  cursor: "not-allowed",
}

const slotChipStyle = {
  minHeight: 40,
  minWidth: 82,
  padding: "8px 11px",
  borderRadius: 999,
  border: "1px solid #cbd5e1",
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  lineHeight: 1.1,
}

const availableSlotChipStyle = {
  background: "#ffffff",
  color: "#0f172a",
}

const selectedSlotChipStyle = {
  background: "#1d4ed8",
  border: "1px solid #1d4ed8",
  color: "#ffffff",
}

const disabledSlotChipStyle = {
  background: "#f8fafc",
  color: "#94a3b8",
  cursor: "not-allowed",
}
